import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Container, Typography, Link, Divider } from "@mui/material";
import SupportFormDialog from "../Support/SupportFormDialog";

/**
 * Footer component
 */
const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [supportOpen, setSupportOpen] = useState(false);

  return (
    <Box
      component="footer"
      sx={{
        py: 3,
        px: 2,
        mt: "auto",
        backgroundColor: "#1a2a3a",
      }}
    >
      <Divider sx={{ mb: 3, borderColor: "rgba(255,255,255,0.15)" }} />
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "center", md: "flex-start" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", md: "flex-start" },
              mb: { xs: 2, md: 0 },
            }}
          >
            <Typography variant="h6" sx={{ color: "#ffffff" }} gutterBottom>
              Hooliah
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
              Share and discover fashion ensembles
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 4 },
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", md: "flex-start" },
              }}
            >
              <Typography variant="subtitle2" sx={{ color: "#ffffff" }} gutterBottom>
                Navigation
              </Typography>
              <Link
                component={RouterLink}
                to="/"
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }}
                underline="hover"
              >
                Home
              </Link>
              <Link
                component={RouterLink}
                to="/search"
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }}
                underline="hover"
              >
                Search
              </Link>
              <Link
                component={RouterLink}
                to="/blog"
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }}
                underline="hover"
              >
                Blog
              </Link>
              <Link
                component={RouterLink}
                to="/about"
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }}
                underline="hover"
              >
                About
              </Link>
              <Link
                component={RouterLink}
                to="/faq"
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }}
                underline="hover"
              >
                FAQ
              </Link>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", md: "flex-start" },
              }}
            >
              <Typography variant="subtitle2" sx={{ color: "#ffffff" }} gutterBottom>
                Legal
              </Typography>
              <Link
                component={RouterLink}
                to="/privacy-policy"
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }}
                underline="hover"
              >
                Privacy Policy
              </Link>
              <Link
                component={RouterLink}
                to="/terms-of-service"
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }}
                underline="hover"
              >
                Terms of Service
              </Link>
              <Link
                component={RouterLink}
                to="/contact"
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }}
                underline="hover"
              >
                Contact
              </Link>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", md: "flex-start" },
              }}
            >
              <Typography variant="subtitle2" sx={{ color: "#ffffff" }} gutterBottom>
                Browse by Style
              </Typography>
              <Link component={RouterLink} to="/styles/casual" sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }} underline="hover">Casual</Link>
              <Link component={RouterLink} to="/styles/formal" sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }} underline="hover">Formal</Link>
              <Link component={RouterLink} to="/styles/street-style" sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }} underline="hover">Street Style</Link>
              <Link component={RouterLink} to="/styles/business" sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }} underline="hover">Business</Link>
              <Link component={RouterLink} to="/occasions/wedding" sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }} underline="hover">Wedding</Link>
              <Link component={RouterLink} to="/occasions/date-night" sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }} underline="hover">Date Night</Link>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", md: "flex-start" },
              }}
            >
              <Typography variant="subtitle2" sx={{ color: "#ffffff" }} gutterBottom>
                Help
              </Typography>
              <Link
                href="/docs/user-guide.html"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }}
                underline="hover"
              >
                User Guide
              </Link>
              <Link
                component={RouterLink}
                to="/settings"
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }}
                underline="hover"
              >
                Settings
              </Link>
              <Link
                component="button"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.blur();
                  setSupportOpen(true);
                }}
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  "&:hover": { color: "#ffffff" },
                  cursor: "pointer",
                  background: "none",
                  border: "none",
                  padding: 0,
                  font: "inherit",
                  textAlign: "left",
                }}
                underline="hover"
              >
                Support
              </Link>
            </Box>


          </Box>
        </Box>

        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
            © {currentYear} Hooliah.com. All rights reserved.
          </Typography>
        </Box>
      </Container>
      <SupportFormDialog open={supportOpen} onClose={() => setSupportOpen(false)} />
    </Box>
  );
};

export default Footer;
